/*****************************************************************
 *  CUSTOMER BUTLER - "MATCH MY PHONE" AND THE LANGUAGE PICKER     *
 *  File: apps/web/src/pages/customer/customerChromeLook.js        *
 *  What it does: the two choices on the right of the HR-style     *
 *  header row, built the same way HR Butler builds its own:       *
 *    1. the look  - Match my phone / Light / Dark / High contrast *
 *       (sets data-theme on <html>, which butler-tokens.css reads)*
 *    2. the language - only languages with every Customer Butler  *
 *       word behind them are offered (today: English).            *
 *  Reads/Writes: localStorage "cb-look" and "cb-lang" (per-viewer *
 *  conveniences); the data-theme and lang attributes on <html>.   *
 *  Never: changes anything on the server.                         *
 *****************************************************************/

/* WHY (2026-10-15, James): "Customer Butler must look and be structured
   EXACTLY like HR Butler." HR's header carries "Match my phone" and a
   language picker. Both are HR-LOCAL in HR's own hr.js (keys "hr-look" and
   "hr-lang"), not a suite service, so Customer Butler gets its own copies,
   built the same way, under its own keys. THE WALL: nothing is imported
   from HR Butler. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE LOOK (Match my phone / Light / Dark / Contrast)*
 *  SECTION 2 - THE LANGUAGE                                       *
 *****************************************************************/

import { useCallback, useEffect, useState } from 'react';

/*****************************************************************
 *  SECTION 1 - THE LOOK                                           *
 *  "Match my phone" = no data-theme on <html>, so the tokens      *
 *  follow the device. Any other choice writes data-theme.         *
 *****************************************************************/

const LOOK_STORAGE_KEY = 'cb-look';     // remembered per person, per device
export const LOOK_MATCH_PHONE = 'system'; // the default: follow the device

// The four looks, in HR's order, with HR's words.
export const LOOK_CHOICES = [
  { key: 'system', words: 'Match my phone' },
  { key: 'light', words: 'Light' },
  { key: 'dark', words: 'Dark' },
  { key: 'contrast', words: 'High contrast' },
];

/* ---- isKnownLook(lookKey) -- true for one of the four looks ---- */
function isKnownLook(lookKey) {
  const knownKeys = LOOK_CHOICES.map((oneChoice) => oneChoice.key);

  return knownKeys.includes(lookKey);
}

/* ---- readStoredLook() -- the saved look, or Match my phone ---- */
export function readStoredLook() {
  try {
    const storedLook = window.localStorage.getItem(LOOK_STORAGE_KEY);

    if (isKnownLook(storedLook)) {
      return storedLook;
    }
  } catch (storageProblem) {
    // storage blocked: Match my phone
  }

  return LOOK_MATCH_PHONE;
}

/* ---- paintLook(lookKey) ------------------------------------------
   Puts the look on <html>. Match my phone takes the attribute
   off, so the device decides (the same rule as HR's applyLook). */
export function paintLook(lookKey) {
  const rootElement = document.documentElement;

  if (lookKey === LOOK_MATCH_PHONE) {
    rootElement.removeAttribute('data-theme');
    return;
  }

  rootElement.setAttribute('data-theme', lookKey);
}

/* ---- useChosenLook() ---------------------------------------------
   Out: [lookKey, chooseLook]. Paints the look while a Customer
   Butler page is open and takes it off again when the page closes,
   so no other page of the suite inherits it.                    */
export function useChosenLook() {
  const [lookKey, setLookKey] = useState(readStoredLook);

  useEffect(() => {
    paintLook(lookKey);

    return () => paintLook(LOOK_MATCH_PHONE);
  }, [lookKey]);

  const chooseLook = useCallback((nextLook) => {
    const safeLook = isKnownLook(nextLook) ? nextLook : LOOK_MATCH_PHONE;

    try {
      window.localStorage.setItem(LOOK_STORAGE_KEY, safeLook);
    } catch (storageProblem) {
      // storage blocked: the choice lasts until the page reloads
    }

    setLookKey(safeLook);
  }, []);

  return [lookKey, chooseLook];
}

/*****************************************************************
 *  SECTION 2 - THE LANGUAGE                                       *
 *  HR's rule: only a language with a full table of words behind  *
 *  it is offered, so nobody picks Spanish and gets English.       *
 *****************************************************************/

// WHY (2026-10-15): every word on the Customer Butler pages is English today.
// Spanish and Russian are added here the day their word tables exist
// (the translation lane does apps first). Offering them now would be a
// button that claims a feature that is not built (Honest UI).
const LANGUAGE_STORAGE_KEY = 'cb-lang';   // remembered per person, per device
export const LANGUAGE_CHOICES = [
  { key: 'en', words: 'English' },
];

/* ---- readStoredLanguage() -- the saved language, or English ---- */
function readStoredLanguage() {
  const offeredKeys = LANGUAGE_CHOICES.map((oneChoice) => oneChoice.key);

  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    if (offeredKeys.includes(storedLanguage)) {
      return storedLanguage;
    }
  } catch (storageProblem) {
    // storage blocked: English
  }

  return 'en';
}

/* ---- useChosenLanguage() -- Out: [languageKey, chooseLanguage] ---- */
export function useChosenLanguage() {
  const [languageKey, setLanguageKey] = useState(readStoredLanguage);

  const chooseLanguage = useCallback((nextLanguage) => {
    const offeredKeys = LANGUAGE_CHOICES.map((oneChoice) => oneChoice.key);
    const safeLanguage = offeredKeys.includes(nextLanguage) ? nextLanguage : 'en';

    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, safeLanguage);
    } catch (storageProblem) {
      // storage blocked: the choice lasts until the page reloads
    }

    setLanguageKey(safeLanguage);
  }, []);

  return [languageKey, chooseLanguage];
}
